import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import InsightPage, {
  StatRow, StatCard, CopyStats, useChartTheme, legendLabel, maxBy, minBy,
  PHONE_CHART_MARGIN, PHONE_X_AXIS, PHONE_Y_AXIS, shortDay,
} from '../../components/insights/InsightPage';
import DateWindowSelect from '../../components/insights/DateWindowSelect';
import { insightsService } from '../../services/apiService';
import { useInsightData } from '../../hooks/useInsightData';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { EMPTY, formatNumber, plural } from '../../utils/format';

const VisitorWeekdays = () => {
  const isMobile = useIsMobile();
  const t = useChartTheme();
  const [days, setDays] = useState(180);
  const { data, loading, error, reload } = useInsightData(() => insightsService.visitorWeekdays(days), [days]);
  const rows = data || [];

  const total = rows.reduce((s, r) => s + r.visitors, 0);
  const busiest = maxBy(rows, 'visitors');
  const quietest = minBy(rows, 'visitors');
  // A ratio needs visitors on the quietest day, and reads as a finding only when the days differ
  const ratio = busiest && quietest && quietest.visitors > 0 ? (busiest.visitors / quietest.visitors).toFixed(1) : null;
  const showRatio = ratio && ratio !== '1.0';
  const busiestShare = total > 0 ? Math.round((busiest.visitors / total) * 100) : 0;

  const copy = total > 0
    ? (showRatio
      ? `${busiest.day} is the busiest day at the front desk: workplaces host ${ratio}× more visitors than on ${quietest.day}. 🛎️`
      : `${busiest.day} is the busiest day at the front desk, with ${busiestShare}% of the week's visitors. 🛎️`)
    : '';

  return (
    <InsightPage
      title="Visitor patterns"
      description="Which day is busiest at the front desk?"
      controls={<DateWindowSelect value={days} onChange={setDays} />}
      loading={loading}
      error={error}
      onRetry={reload}
      ready={data != null}
      empty={total === 0}
      emptyMessage="No visitors booked in this window"
    >
      <StatRow>
        <StatCard label="Busiest day" value={busiest?.day || EMPTY} sub={busiest ? plural(busiest.visitors, 'visitor') : ''} variant="primary" />
        <StatCard label="Quietest day" value={quietest?.day || EMPTY} sub={quietest ? plural(quietest.visitors, 'visitor') : ''} />
        <StatCard label="Busiest against quietest" value={ratio ? `${ratio}×` : EMPTY} sub="as many visitors" />
        <StatCard label="Total visitors" value={formatNumber(total)} />
      </StatRow>

      <Card className="shadow-sm">
        <Card.Header className="bg-light"><h5 className="mb-0">Visitors and check-in rate by weekday</h5></Card.Header>
        <Card.Body>
          <ResponsiveContainer width="100%" height={isMobile ? 300 : 420}>
            <ComposedChart data={rows} margin={isMobile ? PHONE_CHART_MARGIN : { top: 10, right: 30, left: 10, bottom: 20 }}>
              <CartesianGrid {...t.grid} />
              <XAxis dataKey="day" {...t.xAxis} {...(isMobile ? { ...PHONE_X_AXIS, tickFormatter: shortDay } : {})} />
              <YAxis yAxisId="left" allowDecimals={false} {...t.yAxis} {...(isMobile ? PHONE_Y_AXIS : {})} />
              {/* Phones hide the percentage axis to give the bars room; the tooltip still shows the rate */}
              <YAxis yAxisId="right" orientation="right" unit="%" domain={[0, 100]} {...t.yAxis} {...(isMobile ? { hide: true } : {})} />
              <Tooltip {...t.tooltip} formatter={(v, n) => (n === 'Check-in rate' ? `${v}%` : formatNumber(v))} />
              <Legend {...t.legend} formatter={legendLabel} />
              <Bar yAxisId="left" dataKey="visitors" name="Visitors" fill={t.colors.emphasis} />
              <Line yAxisId="right" type="monotone" dataKey="pctCheckedIn" name="Check-in rate" stroke={t.colors.context} strokeWidth={2} dot={{ r: 3, fill: t.colors.context }} />
            </ComposedChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>

      {copy && <CopyStats text={copy} />}
    </InsightPage>
  );
};

export default VisitorWeekdays;
